





































import { Component, Vue } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';
import CustomersList from '@/componentsV2/organisms/CustomersList/CustomersList.vue';
import Page from '@/componentsV2/molecules/Page/Page.vue';
import {
  CustomSelectModel,
  CustomSelectGroup,
  CustomSelectOption
} from '@/components/CustomSelect/CustomSelectModel';

import PerformanceCustomerCenterService from '@/api/PerformanceCustomerCenterService';

import { IMemberRelationship } from '@/models/performance';
import { EBusinessReportType } from '@/models/business';

import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';

import DateUtils from '@/utils/DateUtils';
import eventBus from '@/utils/eventBus';
import segmentIo from '@/utils/segment.io';

@Component({
  components: {
    SectionData,
    CustomersList,
    Trigger,
    Page
  }
})
export default class PortalCustomersList extends Vue {
  performanceCustomerCenterService: PerformanceCustomerCenterService = new PerformanceCustomerCenterService();

  pagination: IPagination = this.prevRouteIsCustomerDetail
    ? this.cachedPagination
    : PaginationUtils.defaultPagination();
  filters: Record<string, any> = this.prevRouteIsCustomerDetail
    ? this.cachedFilters
    : {};
  headers: Record<string, IPaginationTableHeader> = {
    name: {
      label: 'Member',
      sortable: true
    },
    ...(this.showFavorited && {
      isFavoritedBusiness: {
        label: 'Favorited',
        sortable: true
      }
    }),
    ...(this.showCoinsBalance && {
      availableCoins: {
        label: 'Coins Balance',
        sortable: true
      }
    }),
    totalRedemptions: {
      label: 'Redemptions',
      sortable: true
    },
    lastMemberActivityAt: {
      label: 'Last activity',
      sortable: true,
      sortDir: EPaginationSortDir.DESC
    },
    status: {
      label: 'Member status',
      sortable: true
    }
  };
  actionGroup = new CustomSelectGroup(
    this.selectOptions.map(
      ({ value, label }) => new CustomSelectOption(value, label)
    )
  );
  customers: IMemberRelationship[] = this.prevRouteIsCustomerDetail
    ? this.cachedCustomerList
    : [];
  search = {
    placeholder: 'Search by name',
    term:
      this.prevRouteIsCustomerDetail && this.cachedFilters.search
        ? this.cachedFilters.search
        : ''
  };
  firstRequestWasMade = false;
  firstRequestIsLoading = false;

  get tPrefix() {
    return 'performance.customerCenterV2.customersList';
  }

  get gaTracking() {
    return {
      search: {
        eventCategory: 'Member relationships - Search',
        eventAction: 'change'
      },
      select: {
        eventCategory: 'Member relationships - Select',
        eventAction: 'change'
      }
    };
  }

  get selectOptions() {
    if (!this.showFavorited) {
      return [
        { value: '', label: 'All Members' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ];
    }

    return [
      { value: 'active', label: `Member Active` },
      { value: 'inactive', label: `Member Inactive` },
      { value: 'favorited', label: `Favorited you` }
    ];
  }

  get customFilterOptions() {
    return new CustomSelectModel(
      [this.actionGroup],
      this.showFavorited ? `Filter by` : `Member status`,
      false,
      !!this.showFavorited,
      !!this.showFavorited,
      true
    );
  }

  get cachedCustomerList() {
    return this.$store.getters['performance/getCustomerCenterState'].list;
  }

  get cachedPagination() {
    return this.$store.getters['performance/getCustomerCenterState'].config
      .pagination;
  }

  get cachedFilters() {
    return this.$store.getters['performance/getCustomerCenterState'].config
      .filters;
  }

  get prevRouteIsCustomerDetail() {
    return (
      window.previousRouteName &&
      window.previousRouteName === 'performance-customer-center-detail'
    );
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'];
  }

  get reportType() {
    return this.businessInfo.reportType;
  }

  get showCoinsBalance() {
    return this.businessInfo.reportType !== EBusinessReportType.OFFER;
  }

  get showFavorited() {
    return this.businessInfo.reportType !== EBusinessReportType.COIN;
  }

  get description() {
    return 'View a complete list of your members and their profiles to see their activity and relationship with your business.';
  }

  beforeDestroy() {
    eventBus.$off('preffiledCustomerListFilter', this.handleInitialFilter);
    eventBus.$off('customerCenterReloadCustomers', this.loadCustomers);
  }

  mounted() {
    this.handleInitialFilter();
    this.loadCustomers({ isFirstRequest: true, clearList: true });

    eventBus.$on('preffiledCustomerListFilter', this.handleInitialFilter);
    eventBus.$on('customerCenterReloadCustomers', this.loadCustomers);
  }

  handleInitialFilter(filterBy?: string) {
    const { customerListFilter: status } = this.$route.query;
    const { status: cachedStatus = '' } = this.cachedFilters;

    const finalStatus =
      filterBy || status || (this.prevRouteIsCustomerDetail && cachedStatus);

    if (finalStatus) {
      this.filters = Object.assign({}, this.filters, { status: finalStatus });

      this.actionGroup = new CustomSelectGroup(
        this.selectOptions.map(({ value, label }) => {
          if (finalStatus === value) {
            return new CustomSelectOption(value, label, true);
          }

          return new CustomSelectOption(value, label);
        })
      );
    }
  }

  formattedCustomers(customers: IMemberRelationship[]) {
    return customers.map(item => ({
      ...item,
      coinBalance: item.availableCoins,
      redemptions: item.totalRedemptions || 0,
      lastMemberActivityAtLongDate: DateUtils.formattedDate({
        date: item.lastMemberActivityAt,
        applyTimezone: true,
        format: 'MMM D, YYYY h:mm A'
      }),
      lastMemberActivityAt: DateUtils.formattedDate({
        date: item.lastMemberActivityAt,
        applyTimezone: true,
        fromNow: true
      })
    }));
  }

  async loadCustomers({
    clearList = false,
    showLoader = true,
    isFirstRequest = false
  } = {}) {
    let pagination: any = { ...this.pagination };
    if (clearList) {
      pagination = PaginationUtils.defaultPagination();
    }

    if (!pagination.mustCallApi || this.firstRequestIsLoading) return;
    if (isFirstRequest) this.firstRequestIsLoading = true;

    let endpointPath = '/v2/admin/customer';

    if (pagination.next) {
      endpointPath = pagination.next;
    }

    if (showLoader) this.$showLoaderWithLocker();

    const response = await this.performanceCustomerCenterService.getMemberRelationshipsList(
      endpointPath,
      this.filters
    );

    if (showLoader) this.$hideLoader();
    if (isFirstRequest) this.firstRequestIsLoading = false;

    if (response.status !== 200) return;

    const { items, links } = response.data;

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.customers = this.formattedCustomers(items);
      return;
    }

    this.customers = [...this.customers, ...this.formattedCustomers(items)];
    this.firstRequestWasMade = true;

    PaginationUtils.makeNewRequest(
      {
        triggerClass: '.infinite-trigger',
        hasPagination: !!pagination.next
      },
      () => {
        this.loadCustomers({ showLoader: false });
      }
    );
  }

  handleItemClick(rowData: Record<string, any>) {
    const containerScrollElement = document.querySelector(
      '.container-scrollable'
    );

    this.$store.dispatch('performance/SET_CONFIG', {
      context: 'customerCenter',
      config: {
        pagination: this.pagination,
        filters: this.filters,
        ...(containerScrollElement && {
          scrollYPos: containerScrollElement.scrollTop
        })
      }
    });
    this.$store.dispatch('performance/SET_LIST_DATA', {
      context: 'customerCenter',
      list: this.customers
    });
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    segmentIo.track('Members | Clicked Sort', {
      'Which column was sort applied to?': `[${sortDir}] ${
        this.headers[field].label
      } column`
    });

    this.filters = {
      ...this.filters,
      sort: `${field}:${sortDir}`
    };

    this.loadCustomers({ clearList: true });
  }

  handleCustomFilterChange(selectedOptions: CustomSelectOption[]) {
    delete this.filters.status;
    delete this.filters.isFavoritedBusiness;

    if (!this.showFavorited) {
      const status = selectedOptions[0].uuid;
      if (status) {
        this.filters = {
          ...this.filters,
          status
        };
      }
    }

    const hasFavoritedFilter = selectedOptions.filter(
      item => item.uuid === 'favorited'
    ).length;

    if (hasFavoritedFilter) {
      segmentIo.track('Members | Favorited You Filter Applied');

      this.filters = {
        ...this.filters,
        isFavoritedBusiness: true
      };
    }

    const statusFilter = selectedOptions.filter(
      ({ uuid }) => uuid === 'active' || uuid === 'inactive'
    );

    if (statusFilter.length === 1) {
      this.filters = {
        ...this.filters,
        status: statusFilter[0].uuid
      };
    }

    this.loadCustomers({ clearList: true });
  }

  handleSearchFilterChange() {
    delete this.filters.search;

    if (this.search.term) {
      this.filters = {
        ...this.filters,
        search: this.search.term
      };
    }

    this.loadCustomers({ clearList: true });
  }
}
